// Above you's menu (S53's part, Ola, 9 Oct): Check Google's results now, in a disclosure beside the
// heading's ⓘ (activity-table/detail/above-you.blade.php). The site's reads this week come with an open
// row's Above you (its `checks`, AboveYouRead) and with every press, whose answer the part says in its own
// polite live region. Once a read is asked for, a row opened again asks for its Above you again, until an
// answer has come back late enough to hold the new results.

// The Livewire method a press calls (AboveYouRead::CHECK_METHOD); AboveYouMenuTest pins the two together.
export const CHECK_METHOD = "checkGoogleNow";

// How long a read asked for may take to land: the collector looks 3 minutes after it is posted, then
// every 5 (CollectSerpSnapshotsJob). An Above you answered sooner than this after a press may be the old
// one, so its row asks again when it opens.
export const LANDS_WITHIN_MS = 10 * 60 * 1000;

export default function aboveYouPart() {
    return {
        // From the row's place on screen, as detailId() is, so it is a valid id whatever the row's own id holds.
        aboveMenuId(index) {
            return `activity-table-${this.table.id}-above-menu-${index}`;
        },

        aboveChecksId(index) {
            return `activity-table-${this.table.id}-above-checks-${index}`;
        },

        isAboveMenuOpen(id) {
            return this.aboveMenuFor === id;
        },

        // A row's Above you opens with its menu shut, and with no answer to a press made in another row.
        startAboveMenu() {
            this.aboveMenuFor = null;
            this.aboveCheckAnswer = null;
        },

        // A disclosure (accessibility.md §9), as a filter's panel is: the press opens or closes it, and focus
        // moves to the panel's button as it opens, a frame later, once x-show has shown it (afterShow()).
        toggleAboveMenu(id) {
            this.aboveMenuFor = this.aboveMenuFor === id ? null : id;

            if (this.aboveMenuFor !== id) {
                return;
            }

            this.afterShow(() => {
                this.aboveMenuScope(id)?.querySelector("[data-above-check]")?.focus();
            });
        },

        // Escape returns focus to the burger; a press elsewhere leaves it where the press put it.
        closeAboveMenu(returnFocus) {
            const id = this.aboveMenuFor;

            this.aboveMenuFor = null;

            if (returnFocus && id !== null) {
                this.aboveMenuScope(id)?.querySelector("[data-above-menu-button]")?.focus();
            }
        },

        // Tab past the panel closes it and carries on: no trap. Only a move to a named element counts, as
        // leavePanel()'s does: a click on the panel's own words blurs to nothing.
        leaveAboveMenu(event) {
            if (this.aboveMenuFor !== null && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
                this.aboveMenuFor = null;
            }
        },

        // The row's body, which holds its open detail: one body per row (activity-table.blade.php).
        aboveMenuScope(id) {
            const row = this.rowElement(id);

            return (row && (row.closest?.("tbody") || row)) || null;
        },

        // The site's reads this week as last answered, by a press or by any row's Above you, else the row's own.
        aboveChecksOf(row) {
            return this.aboveChecks ?? row.detail?.above?.checks ?? null;
        },

        canCheckAbove(row) {
            return this.aboveChecksOf(row)?.canCheck === true && !this.isAboveCheckPending;
        },

        // The line under the button, in the words the part hands over: how many reads are used, or, with
        // none left, when the next weekly read comes.
        aboveCheckLine(row, words) {
            const checks = this.aboveChecksOf(row);

            if (!checks) {
                return "";
            }

            return checks.canCheck
                ? words.used.replace(":used", checks.used).replace(":cap", checks.cap)
                : words.capped.replace(":date", checks.nextRead);
        },

        // What the last press answered, said in the row it was pressed in; an answer this table does not
        // know reads as a failure, never as nothing.
        aboveCheckSaid(row, words) {
            const answer = this.aboveCheckAnswer;

            if (!answer || answer.id !== row.id) {
                return "";
            }

            return (words[answer.state] ?? words.failed).replace(":date", answer.nextRead);
        },

        // An open row's Above you, kept by keepAbove() (detail.js): the site's reads it carries, and when it
        // was answered, which isAboveDue() compares with the last press.
        keepAboveChecks(id, above) {
            this.aboveAnsweredAt[id] = Date.now();

            if (above.checks && typeof above.checks === "object" && Number.isInteger(above.checks.used)) {
                this.aboveChecks = { ...above.checks };
            }
        },

        // Whether a row opened again asks for its Above you again: its answer came back before a read asked
        // for since could have landed. Never while its answer is still on the way.
        isAboveDue(id) {
            const answeredAt = this.aboveAnsweredAt[id];

            if (this.aboveCheckedAt === null || answeredAt === undefined || this.trendOf(id).state === "loading") {
                return false;
            }

            return answeredAt < this.aboveCheckedAt + LANDS_WITHIN_MS;
        },

        // Check Google's results now. The press is held while one is out, and once the week has no read
        // left; focus stays on the button, which only says so (aria-disabled). The answer's reads replace
        // the site's, and a queued read makes every row ask for its Above you again as it opens.
        async checkAboveNow(id) {
            const row = this.rowById(id);

            if (!row || !this.canCheckAbove(row)) {
                return;
            }

            this.isAboveCheckPending = true;
            // Emptied first, so the same words said twice are heard twice.
            this.aboveCheckAnswer = null;

            let answer;

            try {
                answer = await this.callWire(CHECK_METHOD, [this.table.id]);
            } catch (error) {
                answer = null;
            }

            this.isAboveCheckPending = false;

            const isAnswer = answer !== null && typeof answer === "object" && typeof answer.state === "string";

            if (isAnswer && Number.isInteger(answer.used) && Number.isInteger(answer.cap)) {
                this.aboveChecks = { used: answer.used, cap: answer.cap, canCheck: answer.canCheck === true, nextRead: String(answer.nextRead ?? "") };
            }

            if (isAnswer && answer.state === "queued") {
                this.aboveCheckedAt = Date.now();
            }

            this.aboveCheckAnswer = { id, state: isAnswer ? answer.state : "failed", nextRead: isAnswer ? String(answer.nextRead ?? "") : "" };
        },
    };
}
